import {
  AnalyticsEvent,
  ErrorEvent,
  LogEvent,
  PerformanceEvent,
  TelemetryEvent,
  TelemetryEventType,
  TelemetryProperties,
} from '../events';
import { getClientContext, getTelemetryHostingConfig } from '../telemetryStore';

import { AnalyticsEventInput, LogEventInput } from './eventInputTypes';
import { getNetworkInformation } from './getNetworkInformation';
import translateLegacyEventsProperties from './translateLegacyEventsProperties';

const getErrorEventSpecificProperties = ({ message, stack }: ErrorEvent) => ({
  ...getTelemetryHostingConfig(),
  message,
  stack,
});

const getAnalyticsEventSpecificProperties = () => ({
  scriptRevision: getTelemetryHostingConfig().scriptRevision,
});

const getPerformanceEventSpecificProperties = ({ startTime, duration }: PerformanceEvent) => {
  const { downlink, downlinkMax, effectiveType, rtt, saveData, type } = getNetworkInformation();

  return {
    ...getTelemetryHostingConfig(),
    connectionDownlink: downlink,
    connectionDownlinkMax: downlinkMax,
    connectionEffectiveType: effectiveType,
    connectionRtt: rtt,
    connectionSaveData: saveData,
    connectionType: type,
    duration,
    startTime,
    endTime: duration + startTime,
  };
};

const getEventTypeSpecificProperties = async (event: TelemetryEvent) => {
  switch (event.type) {
    case TelemetryEventType.Analytics:
      return getAnalyticsEventSpecificProperties();
    case TelemetryEventType.Error:
      return getErrorEventSpecificProperties(event);
    case TelemetryEventType.Info:
      return getTelemetryHostingConfig();
    default:
      return getPerformanceEventSpecificProperties(event);
  }
};

const getTelemetryEventProperties = async (event: TelemetryEvent): Promise<TelemetryProperties> => {
  const eventTypeSpecificProperties = await getEventTypeSpecificProperties(event);
  const translatedProperties = translateLegacyEventsProperties(event);
  const { deviceType, hostClientType, osName, clientFeedLayout } = getClientContext();

  return {
    ...eventTypeSpecificProperties,
    ...translatedProperties,
    ...(deviceType && { deviceType: deviceType.toLowerCase() }),
    ...(hostClientType && { hostClientType: hostClientType.toLowerCase() }),
    ...(osName && { OsName: osName.toLowerCase() }),
    ...(clientFeedLayout && { clientFeedLayout: clientFeedLayout.toLowerCase() }),
  };
};

type GetAnalyticsEventInput = (event: AnalyticsEvent) => Promise<AnalyticsEventInput>;

export const getAnalyticsEventInput: GetAnalyticsEventInput = async (analyticsEvent) => ({
  name: analyticsEvent.name,
  properties: {
    ...(await getTelemetryEventProperties(analyticsEvent)),
    client_event_time: analyticsEvent.occurredAt,
  },
});

type GetLogEventInput = (event: LogEvent) => Promise<LogEventInput>;

export const getLogEventInput: GetLogEventInput = async (event) => ({
  EventName: event.name,
  ClientTimeStamp: Number(event.occurredAt),
  LogType: event.type.toLowerCase(),
  Parameters: {
    ...(await getTelemetryEventProperties(event)),
    client_event_time: event.occurredAt,
  },
});
